import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Stack, CircularProgress } from '@mui/material';
import PinInput from 'react-pin-input';
import { useSignUpStore } from '../../../application/store/SignUpStore';
import { container } from '@infrastructure/container';
import { useAuth } from '@shared/hooks/useAuth';
import ResendCodeButton from '@features/sign-in/components/ResendCodeButton';

const pinLength = 6;

const SignUpValidate = () => {
    const { email, userId } = useSignUpStore();
    const [code] = useState('');
    const [error, setError] = useState<string | null>(null);

    const [isVerifying, setIsVerifying] = useState(false);
    const navigate = useNavigate();
    const { setEmail: storeEmail } = useSignUpStore();
    const { logout } = useAuth();

    const verifyCode = async (code: string) => {
        if (code.length !== pinLength) return;

        setIsVerifying(true);
        setError(null);

        try {
            await logout();

            const result = await container.authRepository.verifyCode(userId!, code);
            if (result.status === 'success') {
                storeEmail(email!);
                navigate('/sign-up/password');
            } else {
                setError('Invalid verification code');
            }
        } catch (error) {
            setError('Verification failed');
        } finally {
            setIsVerifying(false);
        }
    };

    const sendCode = async () => {
        const result = await container.authRepository.sendCode(email!);

        if (result.status === 'success' && result.data?.userId) {
            useSignUpStore.getState().setUserId(result.data.userId);
            setError(null);
        } else {
            setError('Failed to send code');
        }
    };


    return (
        <Box>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
                Verify your email
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                We sent a verification code to <strong>{email}</strong>
            </Typography>

            <PinInput
                length={pinLength}
                focus
                type="numeric"
                inputMode="numeric"
                disabled={isVerifying}
                style={{ marginBottom: '24px' }}
                inputStyle={{
                    borderColor: error ? '#d32f2f' : '#e0e0e0',
                    borderRadius: '4px',
                    margin: '0 4px',
                    width: '40px',
                    height: '40px',
                    opacity: isVerifying ? 0.6 : 1
                }}
                onComplete={(value) => verifyCode(value)}
                onChange={() => setError(null)}
            />

            {error && (
                <Typography color="error" variant="caption" sx={{ mb: 2, display: 'block' }}>
                    {error}
                </Typography>
            )}

            <ResendCodeButton onResend={sendCode} sx={{ mb: 3 }} />


            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    size="medium"
                >
                    Back
                </Button>

                <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    size="medium"
                    disabled={code.length !== pinLength || isVerifying}
                >
                    {isVerifying ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} color="inherit" />
                            Verifying...
                        </Box>
                    ) : (
                        'Continue'
                    )}
                </Button>
            </Stack>

        </Box>
    );
};

export default SignUpValidate; 